import { api } from '@/api'
import qs from 'qs'
import { snakeCase } from 'lodash'

interface Params {
  include?: string
  q: any
  page: number
  perPage: number
  excludeMailTasks?: boolean
  taskId: number
  cancellableWoCount?: boolean
  withoutCount?:boolean
  excludePayload?: boolean
  includeReceivableData?: boolean
  includePayableData?: boolean
  receivable?: boolean
  payable?: boolean
  newBackgroundTask?: boolean
  ids?: number[]
  sort?: any
}
interface CustomReportHeaderInterface {
  jobType: string,
  type: string,
  reportType: string,
  customReportType: string
}
export default {
  get: ({ include, q, page, perPage, excludeMailTasks }:Params) => {
    return api.$get('/background_tasks', {
      params: {
        include,
        q,
        page,
        per_page: perPage,
        exclude_mail_tasks: excludeMailTasks
      },
      paramsSerializer: params => {
        return qs.stringify(params, { arrayFormat: 'brackets' })
      }
    })
  },
  getById: (id: string | number, include: string, showInfo?: boolean, excludePayload?: boolean, newBackgroundTask?: boolean, includeWorkOrdersCount?: boolean) => {
    return api.$get(`/background_tasks/${id}/get_stats`, {
      params: { include, showInfo, excludePayload, newBackgroundTask, includeWorkOrdersCount },
      paramsSerializer: params => {
        return qs.stringify(params, { arrayFormat: 'brackets' })
      }
    })
  },
  post: (backgroundTaskParams: FormData | any) => {
    let params = backgroundTaskParams
    if (!(backgroundTaskParams instanceof FormData)) {
      params = new FormData()
      const paramKeys = Object.keys(backgroundTaskParams)
      for (let k = 0; k < paramKeys.length; k++) {
        const val = backgroundTaskParams[`${paramKeys[k]}`]
        if (val === undefined || typeof val === 'string') {
          params.append(`background_task[${snakeCase(paramKeys[k])}]`, val)
        } else {
          for (let i = 0; i < val.length; i++) {
            params.append(`background_task[${snakeCase(paramKeys[k])}][]`, val[i])
          }
        }
      }
    }
    return api.$post('/background_tasks',
      params, { headers: { 'Content-Type': 'multipart/form-data' } }
    )
  },

  getCancellableWOs: ({ include, q, page, perPage, taskId, cancellableWoCount }: Params) => {
    return api.$get(`/bulk_cancel_work_orders/${taskId}/cancellable_work_orders`, {
      params: {
        include,
        q,
        page,
        per_page: perPage,
        cancellable_wo_count: cancellableWoCount
      },
      paramsSerializer: params => {
        return qs.stringify(params, { arrayFormat: 'brackets' })
      }
    })
  },
  getNonCancellableWOs: ({ include, q, page, perPage, taskId }: Params) => {
    return api.$get(`/bulk_cancel_work_orders/${taskId}/non_cancellable_work_orders`, {
      params: {
        include,
        q,
        page,
        per_page: perPage
      },
      paramsSerializer: params => {
        return qs.stringify(params, { arrayFormat: 'brackets' })
      }
    })
  },
  patch: (requestBody: any, params: any) => {
    const { id } = params
    return api.$patch(`/background_tasks/${id}/process_task`, {
      background_task: { ...requestBody }
    }, {
      headers: {
        'Content-Type': 'application/json'
      },
      paramsSerializer: (params: any) => {
        return qs.stringify(params)
      }
    })
  },
  getErrorReportById: (id: string | number) => {
    return api.$get(`/background_tasks/${id}/download_error_file`)
  },
  getCustomReportHeaders: (body: CustomReportHeaderInterface) => {
    return api.$get('/background_tasks/custom_headers', {
      params: body,
      paramsSerializer: params => {
        return qs.stringify(params)
      }

    })
  },
  getSuccessReportById: (id: string | number) => {
    return api.$get(`/background_tasks/${id}/download_success_file`)
  },
  getNonCancellableWOsWithReasons: ({ taskId }: Params) => {
    return api.$get(`/bulk_cancel_work_orders/${taskId}/non_cancellable_reasons_with_work_orders`)
  },
  update: (id: number, requestBody: { groupBy?: string, pdfTemplate?: string, jobType: string, recalculateStats?: boolean, receivable?: boolean, payable?: boolean, excludePayload?: boolean, tripReportConfig?: string, newBackgroundTask?: boolean }, queryParams?: { includeReceivableData?: boolean, includePayableData?: boolean }) => {
    return api.$patch(`/background_tasks/${id}`, {
      backgroundTask: requestBody,
      ...queryParams
    }, {
      paramsSerializer: (params: any) => {
        return qs.stringify(params)
      }
    })
  },
  childBackgroundTasks: (params: Params) => {
    const parentId = params.taskId
    return api.$get(`/background_tasks/${parentId}/child_background_tasks`, {
      params: { ...params },
      paramsSerializer: params => {
        return qs.stringify(params)
      }
    })
  },
  getBulkInvoiceTasks: (bulkInvoiceTaskId: number, params: Params) => {
    return api.$get(`/background_tasks/${bulkInvoiceTaskId}/bulk_invoice_tasks`, {
      params: { ...params },
      paramsSerializer: params => {
        return qs.stringify(params, { arrayFormat: 'brackets' })
      }
    })
  },
  bulkDeleteChildBackgroundTasks: (params: { taskId: number, excludePayload?: boolean, newBackgroundTask?: boolean, ids?: number[], jobType: string, idNotIn?: number[] }) => {
    const parentId = params.taskId
    return api.$delete(`/background_tasks/${parentId}/bulk_destroy_child_background_tasks`, { params })
  },
  bulkUpdateChildBackgroundTasks: (id: number, requestBody: { newBackgroundTask?: boolean, ids?: number[], jobType: string, idNotIn?: number[] }) => {
    return api.$patch(`/background_tasks/${id}/bulk_update_child_background_tasks`, {
      backgroundTask: requestBody
    }, {
      paramsSerializer: (params: any) => {
        return qs.stringify(params)
      }
    })
  },
  actionableChildBackgroundTasks: (params: { taskId: number, newBackgroundTask: boolean, jobType: string, field: string, ids?: number[], idNotIn?: number[] }) => {
    const parentId = params.taskId
    return api.$get(`/background_tasks/${parentId}/actionable_child_background_tasks`, {
      params: { ...params }
    })
  },
  duplicateChildrenFromIds: (id: number, requestBody: { newBackgroundTask?: boolean, ids?: number[], jobType: string, idNotIn?: number[], sort?: any, perPage?: number, page?: number }) => {
    return api.$post(`/background_tasks/${id}/duplicate_children_from_ids`, {
      backgroundTask: requestBody
    }, {
      paramsSerializer: (params: any) => {
        return qs.stringify(params)
      }
    })
  }
}
